import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  CircularProgress,
  Backdrop,
  LinearProgress,
  Snackbar,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  useTheme
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { bg, border, gradient, text as textColors, adaptiveColor, statusBg } from '../../utils/darkModeAdapter';
import { motion } from 'framer-motion';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PersonIcon from '@mui/icons-material/Person';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import WorkIcon from '@mui/icons-material/Work';
import BlockIcon from '@mui/icons-material/Block';
import GroupIcon from '@mui/icons-material/Group';
import DoneIcon from '@mui/icons-material/Done';
import PendingIcon from '@mui/icons-material/Pending';
import InfoIcon from '@mui/icons-material/Info';
import AssignmentIcon from '@mui/icons-material/Assignment';
import MicIcon from '@mui/icons-material/Mic';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';

import InterviewCheckSheet from './InterviewCheckSheet';
import EvaluationResult from './EvaluationResult';
import LoadingScreen from '../../components/LoadingScreen';
import { showError } from '../../utils/errorHandler';
import axios from 'axios';

// 音声録音関連のコンポーネントとサービス
import { AudioRecorderPanel } from './components/AudioRecorder';
import { audioService } from './services/audioAPI';

// 面接アシスト拡張機能
import { TemplateSelector, BARSEvaluationForm } from './components/Templates';
import { BiasAnalysisPanel } from './components/BiasDetection';
import { ConfidenceAnalysisPanel } from './components/ConfidenceAnalysis';


const MainContent = ({
  evaluationCategories,
  setEvaluationCategories,
  formData,
  setFormData,
  currentCheckSheetId,
  setCurrentCheckSheetId,
  saveStateToLocalStorage,
  messages,
  setMessages,
  setBrushupLoading,
  interviewAPI,
  brushupLoading,
  nextStageLoading,
  setNextStageLoading,
  onBackToCheckSheetList,
  // 面接状態を親から受け取る
  isInterviewCompleted: parentIsInterviewCompleted,
  setIsInterviewCompleted: parentSetIsInterviewCompleted,
  evaluationResult: parentEvaluationResult,
  setEvaluationResult: parentSetEvaluationResult,
  applicantInfo: parentApplicantInfo,
  setApplicantInfo: parentSetApplicantInfo,
  interviewerInfo: parentInterviewerInfo,
  setInterviewerInfo: parentSetInterviewerInfo
}) => {
  const { user } = useAuth();
  const theme = useTheme();
  const isChildAccount = user?.role === 'interview_only';

  // 評価項目の追加
  const [categoryNameInput, setCategoryNameInput] = useState('');
  
  // 質問IDの管理
  const [questionIds, setQuestionIds] = useState(() => {
    const savedQuestionIds = localStorage.getItem(`checksheet_questionIds_${currentCheckSheetId}`);
    return savedQuestionIds ? JSON.parse(savedQuestionIds) : {};
  });
  
  const handleAddCategory = async () => {
    if (!categoryNameInput.trim()) return;
    
    try {
      const newCategory = {
        name: categoryNameInput.trim(),
        items: [],
        source: 'user_added'
      };
      
      // カテゴリをUIに追加
      setEvaluationCategories(prev => [...prev, newCategory]);
      setCategoryNameInput('');
      
      // 状態を保存
      saveStateToLocalStorage({
        evaluationCategories: [...evaluationCategories, newCategory]
      });
      
      // 成功メッセージを表示
      setSnackbar({
        open: true,
        message: '評価項目を追加しました',
        severity: 'success'
      });
    } catch (error) {
      console.error('評価項目の追加に失敗:', error);
      setSnackbar({
        open: true,
        message: '評価項目の追加に失敗しました',
        severity: 'error'
      });
    }
  };

  // 想定質問の追加
  const [selectedCategory, setSelectedCategory] = useState(0);
  const [itemTextInput, setItemTextInput] = useState('');
  
  const handleAddItem = async () => {
    if (!itemTextInput.trim() || selectedCategory === undefined) return;
    
    try {
      // 新しい質問をデータベースに保存
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/interview_assistant/questions`,
        {
          check_sheet_id: currentCheckSheetId,
          question: itemTextInput.trim(),
          category: evaluationCategories[selectedCategory].name,
          is_required: false,
          display_order: evaluationCategories[selectedCategory].items.length + 1
        },
        {
          headers: {
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );

      const questionData = response.data;
      
      // 一時的なIDを生成（タイムスタンプベース）
      const tempId = `item${Date.now()}`;
      
      const newItem = {
        id: tempId,
        text: itemTextInput.trim(),
        highlighted: false,
        questionId: questionData.id // データベースから返された質問IDを保存
      };
      
      // 評価カテゴリを更新
      setEvaluationCategories(prev => {
        const updated = [...prev];
        updated[selectedCategory].items.push(newItem);
        return updated;
      });
      
      // 質問IDをローカルストレージにも保存
      const updatedQuestionIds = {
        ...questionIds,
        [tempId]: questionData.id
      };
      setQuestionIds(updatedQuestionIds);
      localStorage.setItem(`checksheet_questionIds_${currentCheckSheetId}`, JSON.stringify(updatedQuestionIds));
      
      setItemTextInput('');
      
      // 成功メッセージを表示
      setSnackbar({
        open: true,
        message: '質問を追加しました',
        severity: 'success'
      });

    } catch (error) {
      console.error('質問の追加に失敗:', error);
      setSnackbar({
        open: true,
        message: '質問の追加に失敗しました',
        severity: 'error'
      });
    }
  };

  // 応募者と面接者の情報は親から受け取るpropsを使用
  const applicantInfo = parentApplicantInfo || {
    name: '',
    nameKana: '',
    desiredPosition: '',
    notes: ''
  };
  const setApplicantInfo = parentSetApplicantInfo;

  const interviewerInfo = parentInterviewerInfo || {
    name: '',
    nameKana: '',
    department: '',
    position: ''
  };
  const setInterviewerInfo = parentSetInterviewerInfo;

  // 面接履歴
  const [interviewHistory, setInterviewHistory] = useState([]);
  
  // 現在の面接段階
  const [currentStage, setCurrentStage] = useState(() => {
    // ローカルストレージから復元を試みる
    const savedState = localStorage.getItem(`interviewState_${currentCheckSheetId}`);
    if (savedState) {
      const parsedState = JSON.parse(savedState);
      return parsedState.currentStage || 0;
    }
    return 0;
  });

  // 面接完了状態は親から受け取るpropsを使用
  const isInterviewCompleted = parentIsInterviewCompleted || false;
  const setIsInterviewCompleted = parentSetIsInterviewCompleted;
  // 子アカウント用チェックシート保存完了状態
  const [isChildAccountSaved, setIsChildAccountSaved] = useState(false);
  // 評価結果は親から受け取るpropsを使用
  const evaluationResult = parentEvaluationResult;
  const setEvaluationResult = parentSetEvaluationResult;

  // 親/子アカウント回答切り替え用のstate
  const [answersViewMode, setAnswersViewMode] = useState('parent'); // 'parent' | 'child_0' | 'child_1' ...
  const [childAccountsAnswers, setChildAccountsAnswers] = useState(null);
  const [loadingChildAnswers, setLoadingChildAnswers] = useState(false);

  // 評価中の状態
  const [evaluatingInterview, setEvaluatingInterview] = useState(false);
  // 評価プロセスの進行状態
  const [evaluationProgress, setEvaluationProgress] = useState('');
  // 評価ステップ（0: 開始前, 1: 回答保存中, 2: 評価生成中, 3: 完了）
  const [evaluationStep, setEvaluationStep] = useState(0);

  // 評価モード選択（'checksheet' | 'audio' | 'both'）
  const [evaluationMode, setEvaluationMode] = useState(() => {
    const savedMode = localStorage.getItem(`evaluationMode_${currentCheckSheetId}`);
    return savedMode || 'both';
  });

  // 評価モード変更時にローカルストレージに保存
  useEffect(() => {
    if (currentCheckSheetId) {
      localStorage.setItem(`evaluationMode_${currentCheckSheetId}`, evaluationMode);
    }
  }, [evaluationMode, currentCheckSheetId]);

  // 評価モードの変更ハンドラ
  const handleEvaluationModeChange = (event, newMode) => {
    if (newMode !== null) {
      setEvaluationMode(newMode);
    }
  };
  
  // InterviewCheckSheetからの評価データを保持
  const [interviewCheckSheetState, setInterviewCheckSheetState] = useState({
    ratings: {},
    memos: {},
    questionIds: {}
  });

  const [historyLoading, setHistoryLoading] = useState(false);
  
  // チェックシートへの参照を保持
  const checkSheetRef = useRef(null);

  // スクロール制御用のref
  const evaluationResultRef = useRef(null);
  const childAccountSavedRef = useRef(null);

  // バリデーション用のstate
  const [errors, setErrors] = useState({});

  // Snackbar の state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // 子アカウント完了状況確認ダイアログの state
  const [childAccountDialog, setChildAccountDialog] = useState({
    open: false,
    childAccounts: [],
    completedCount: 0,
    totalCount: 0
  });

  // Snackbar を閉じる処理
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // チェックシート評価の進捗計算
  const calculateCheckSheetProgress = () => {
    if (!evaluationCategories || evaluationCategories.length === 0) {
      return { completed: 0, total: 0, percentage: 0 };
    }

    let totalQuestions = 0;
    let completedQuestions = 0;

    evaluationCategories.forEach(category => {
      if (category.items) {
        category.items.forEach(item => {
          totalQuestions++;
          // ratingが設定されているかチェック（interviewCheckSheetStateまたはitem.rating）
          const hasRating = interviewCheckSheetState?.ratings?.[item.id] || item.rating;
          if (hasRating) {
            completedQuestions++;
          }
        });
      }
    });

    const percentage = totalQuestions > 0 ? Math.round((completedQuestions / totalQuestions) * 100) : 0;
    return { completed: completedQuestions, total: totalQuestions, percentage };
  };

  // 進捗状態を取得
  const checkSheetProgress = calculateCheckSheetProgress();

  // 単一CTAの決定ロジック
  const getPrimaryAction = () => {
    // 応募者情報が未入力（子アカウントは除く）
    if (!isChildAccount && (!parentApplicantInfo?.name?.trim())) {
      return {
        label: '応募者情報を入力',
        description: '応募者の氏名を入力してください',
        variant: 'outlined',
        color: 'primary'
      };
    }

    // 面接者情報が未入力
    if (!parentInterviewerInfo?.name?.trim()) {
      return {
        label: '面接者情報を入力',
        description: '面接担当者の氏名を入力してください',
        variant: 'outlined',
        color: 'primary'
      };
    }

    // チェックシートモードで評価が未完了
    if ((evaluationMode === 'checksheet' || evaluationMode === 'both') && checkSheetProgress.percentage < 100) {
      return {
        label: `チェックシートを評価 (${checkSheetProgress.percentage}%)`,
        description: `${checkSheetProgress.completed}/${checkSheetProgress.total} 質問に回答済み`,
        variant: 'contained',
        color: 'primary',
        showProgress: true,
        progress: checkSheetProgress.percentage
      };
    }

    // 面接完了可能
    if (!isInterviewCompleted) {
      return {
        label: isChildAccount ? 'チェックシートを保存' : '面接を完了して評価を生成',
        description: isChildAccount ? '入力内容を保存します' : 'AIが面接内容を分析して評価を生成します',
        variant: 'contained',
        color: 'success'
      };
    }

    return null;
  };

  const primaryAction = getPrimaryAction();

  // バリデーション関数
  const validateForms = () => {
    const newErrors = {};
    const errorMessages = [];
    
    // 応募者情報のバリデーション
    if (!isChildAccount) {
      // 親アカウント：通常のバリデーション
      if (!parentApplicantInfo.name.trim()) {
        newErrors.applicantName = '応募者の氏名を入力してください';
        errorMessages.push('応募者の氏名を入力してください');
      }
      if (!parentApplicantInfo.nameKana.trim()) {
        newErrors.applicantNameKana = '応募者のフリガナを入力してください';
        errorMessages.push('応募者のフリガナを入力してください');
      }
      if (!parentApplicantInfo.desiredPosition.trim()) {
        newErrors.applicantDesiredPosition = '希望の役職を入力してください';
        errorMessages.push('希望の役職を入力してください');
      }
    } else {
      // 子アカウント：応募者情報が取得されているかチェック
      // 子アカウントの場合、基本的な応募者名があれば OK（モック情報でも許可）
      if (!parentApplicantInfo.name?.trim() || parentApplicantInfo.name.trim() === '候補者名未設定') {
        newErrors.applicantName = '応募者情報が取得できていません。ページを再読み込みしてください';
        errorMessages.push('応募者情報が取得できていません。ページを再読み込みしてください');
      }
    }
    
    // 面接者情報のバリデーション
    if (!parentInterviewerInfo.name.trim()) {
      newErrors.interviewerName = '面接者の氏名を入力してください';
      errorMessages.push('面接者の氏名を入力してください');
    }
    if (!parentInterviewerInfo.department.trim()) {
      newErrors.interviewerDepartment = '所属部署を入力してください';
      errorMessages.push('所属部署を入力してください');
    }
    if (!parentInterviewerInfo.position.trim()) {
      newErrors.interviewerPosition = '役職を入力してください';
      errorMessages.push('役職を入力してください');
    }
    
    setErrors(newErrors);
    return { isValid: errorMessages.length === 0, errorMessages };
  };

  // 応募者・面接官情報は親コンポーネントで管理されるため、ローカルストレージ保存は不要

  // 入力が変更されたら対応するエラーをクリア
  useEffect(() => {
    if (Object.keys(errors).length === 0) return;

    const newErrors = { ...errors };
    let hasChanges = false;

    // 応募者情報のエラーをクリア
    if (parentApplicantInfo?.name?.trim() && newErrors.applicantName) {
      delete newErrors.applicantName;
      hasChanges = true;
    }
    if (parentApplicantInfo?.nameKana?.trim() && newErrors.applicantNameKana) {
      delete newErrors.applicantNameKana;
      hasChanges = true;
    }
    if (parentApplicantInfo?.desiredPosition?.trim() && newErrors.applicantDesiredPosition) {
      delete newErrors.applicantDesiredPosition;
      hasChanges = true;
    }

    // 面接者情報のエラーをクリア
    if (parentInterviewerInfo?.name?.trim() && newErrors.interviewerName) {
      delete newErrors.interviewerName;
      hasChanges = true;
    }
    if (parentInterviewerInfo?.department?.trim() && newErrors.interviewerDepartment) {
      delete newErrors.interviewerDepartment;
      hasChanges = true;
    }
    if (parentInterviewerInfo?.position?.trim() && newErrors.interviewerPosition) {
      delete newErrors.interviewerPosition;
      hasChanges = true;
    }

    if (hasChanges) {
      setErrors(newErrors);
    }
  }, [parentApplicantInfo, parentInterviewerInfo, errors]);

  // currentStageが変更されたらローカルストレージに保存
  useEffect(() => {
    if (!currentCheckSheetId) return;
    const stateToSave = {
      currentStage,
      formData,
      interviewStages: formData.interviewStages
    };
    localStorage.setItem(`interviewState_${currentCheckSheetId}`, JSON.stringify(stateToSave));
    
  }, [currentStage, currentCheckSheetId, formData]);

  // 保存された状態の復元（親コンポーネントで主要データは復元されるため、ここでは最小限のみ）
  useEffect(() => {
    if (window.savedInterviewState) {
      if (window.savedInterviewState.interviewHistory) {
        setInterviewHistory(window.savedInterviewState.interviewHistory);
      }
      if (window.savedInterviewState.currentStage !== undefined) {
        setCurrentStage(window.savedInterviewState.currentStage);
      }
      // 子アカウント保存完了状態の復元
      if (window.savedInterviewState.isChildAccountSaved !== undefined) {
        setIsChildAccountSaved(window.savedInterviewState.isChildAccountSaved);
      }
    }
  }, []);

  const [skipCategoryInit, setSkipCategoryInit] = useState(false);

  // 評価カテゴリの初期化
  useEffect(() => {
    const initializeCategories = async () => {
      if (!currentCheckSheetId || skipCategoryInit || historyLoading) return;

      try {
        // チェックシート情報を取得（answers含む）
        const checkSheetResponse = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/interview_assistant/check-sheets/${currentCheckSheetId}`,
          {
            headers: {
              'Content-Type': 'application/json'
            },
            withCredentials: true
          }
        );
        const checkSheetData = checkSheetResponse.data;

        // 回答データをマップ化（question_id → {rating, notes}）
        const answerMap = {};
        if (checkSheetData.answers && Array.isArray(checkSheetData.answers)) {
          checkSheetData.answers.forEach(ans => {
            answerMap[ans.question_id] = {
              rating: ans.rating,
              notes: ans.notes
            };
          });
        }

        // 質問を取得
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/interview_assistant/check-sheets/${currentCheckSheetId}/questions`,
          {
            headers: {

              'Content-Type': 'application/json'
            },
            withCredentials: true
          }
        );

        const existingQuestions = response.data;

        // 質問が存在しない場合はデフォルトのカテゴリを使用
        if (!existingQuestions || existingQuestions.length === 0) {
          
          // デフォルトのカテゴリを設定（APIで質問が生成されていない場合）
          const defaultCategories = [
            {
              name: 'スキル適合度',
              items: [
                { id: 'skill_1', text: 'あなたの専門知識や技術を教えてください', highlighted: false },
                { id: 'skill_2', text: '前職での成果を具体的に教えてください', highlighted: false }
              ]
            },
            {
              name: '人物評価', 
              items: [
                { id: 'personality_1', text: 'チームで働く際に大切にしていることは何ですか？', highlighted: false },
                { id: 'personality_2', text: '困難な課題に直面したとき、どう行動しましたか？', highlighted: false }
              ]
            },
            {
              name: 'コミュニケーションスキル',
              items: [
                { id: 'comm_1', text: '自己紹介をお願いします', highlighted: false },
                { id: 'comm_2', text: 'チームで意見が合わないとき、どう対処しますか？', highlighted: false }
              ]
            }
          ];
          
          setEvaluationCategories(defaultCategories);
          return;
        }

        const questionIdMap = {};

        // 質問からカテゴリを抽出して重複を排除
        const uniqueCategories = [...new Set(existingQuestions.map(q => q.category))];
        
        // カテゴリごとに質問をグループ化
        const categories = uniqueCategories.map(categoryName => {
          const categoryQuestions = existingQuestions.filter(q => q.category === categoryName);
          const items = categoryQuestions.map(question => {
            const itemId = `item${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            questionIdMap[itemId] = question.id;

            // 回答データがあればratingとnotesを追加
            const answer = answerMap[question.id];
            const item = {
              id: itemId,
              text: question.question,
              highlighted: false,
              questionId: question.id
            };

            if (answer) {
              if (answer.rating !== undefined && answer.rating !== null) {
                item.rating = String(answer.rating);
              }
              if (answer.notes) {
                item.notes = answer.notes;
              }
            }

            return item;
          });

          return {
            name: categoryName,
            items: items,
            minQuestions: 5
          };
        });

        if (categories.length > 0) {
          setEvaluationCategories(categories);
          setQuestionIds(questionIdMap);
          localStorage.setItem(`checksheet_questionIds_${currentCheckSheetId}`, JSON.stringify(questionIdMap));

          // 復元されたrating/notesの数を確認
          const itemsWithRating = categories.reduce((count, cat) =>
            count + cat.items.filter(item => item.rating !== undefined).length, 0
          );
          const itemsWithNotes = categories.reduce((count, cat) =>
            count + cat.items.filter(item => item.notes).length, 0
          );

        } else {
          // フォールバック: デフォルトカテゴリを設定
          const defaultCategories = [
            {
              name: 'スキル適合度',
              items: [
                { id: 'skill_1', text: 'あなたの専門知識や技術を教えてください', highlighted: false },
                { id: 'skill_2', text: '前職での成果を具体的に教えてください', highlighted: false }
              ]
            },
            {
              name: '人物評価',
              items: [
                { id: 'personality_1', text: 'チームで働く際に大切にしていることは何ですか？', highlighted: false },
                { id: 'personality_2', text: '困難な課題に直面したとき、どう行動しましたか？', highlighted: false }
              ]
            }
          ];
          setEvaluationCategories(defaultCategories);
        }

      } catch (error) {
        console.error('評価カテゴリの初期化に失敗:', error);
        
        // エラー時もデフォルトカテゴリを設定
        const defaultCategories = [
          {
            name: 'スキル適合度',
            items: [
              { id: 'skill_1', text: 'あなたの専門知識や技術を教えてください', highlighted: false },
              { id: 'skill_2', text: '前職での成果を具体的に教えてください', highlighted: false }
            ]
          },
          {
            name: '人物評価',
            items: [
              { id: 'personality_1', text: 'チームで働く際に大切にしていることは何ですか？', highlighted: false },
              { id: 'personality_2', text: '困難な課題に直面したとき、どう行動しましたか？', highlighted: false }
            ]
          }
        ];
        setEvaluationCategories(defaultCategories);
        
        setSnackbar({
          open: true,
          message: '評価カテゴリの初期化に失敗しました。デフォルトカテゴリを使用します。',
          severity: 'warning'
        });
      }
    };

    initializeCategories();
  }, [currentCheckSheetId, historyLoading, skipCategoryInit]);

  
  // チェックシート詳細取得（ステージ情報と完了状態）
  const handleShowHistory = async (checkSheetId) => {
    setHistoryLoading(true);
    setSkipCategoryInit(true);
    try {
      setEvaluatingInterview(true);
      setEvaluationProgress('チェックシートを読み込んでいます...');
      setTimeout(() => setSkipCategoryInit(false), 300);
  
      // 詳細データを取得
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/interview_assistant/check-sheets/${checkSheetId}`,
        {
          headers: {
            
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );
  
      const checkSheet = response.data;
    
      // 2. 質問リストも取得
      const questionsResponse = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/interview_assistant/check-sheets/${checkSheetId}/questions`,
        {
          headers: {
            
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );
      const questions = questionsResponse.data;

      // 3. 回答をquestion_idでマッピング
      const answerMap = {};
      if (checkSheet.answers) {
        checkSheet.answers.forEach(ans => {
          answerMap[ans.question_id] = ans;
        });
      }

      // 4. 質問カテゴリごとにitem.rating,item.notesをマージしてカテゴリ構築
      const questionCategories = questions.reduce((acc, question) => {
        const category = question.category || 'その他';
        if (!acc[category]) acc[category] = [];
        const ans = answerMap[question.id];
        acc[category].push({
          id: `q_${question.id}`,
          text: question.question,
          highlighted: question.is_required,
          questionId: question.id,
          rating: ans ? String(ans.rating) : undefined,
          notes: ans ? ans.notes : ''
        });
        return acc;
      }, {});

      const categories = [];
      Object.entries(questionCategories).forEach(([category, items]) => {
        categories.push({
          name: category,
          items
        });
      });
      // ======= ここが面接プロセス情報の重要ポイント =======
      setFormData(prev => ({
        ...prev,
        interview_stage: checkSheet.interview_stage,
        interview_stage_order: checkSheet.interview_stage_order,
        total_stages: checkSheet.total_stages,
        is_final_stage: checkSheet.is_final_stage,
        interviewStages: Array.from(
          { length: checkSheet.total_stages || 1 },
          (_, i) =>
            i === (checkSheet.total_stages - 1)
              ? '最終面接'
              : `${i + 1}次面接`
        )
      }));
  
      setCurrentStage(
        typeof checkSheet.interview_stage_order === "number"
          ? checkSheet.interview_stage_order - 1
          : 0
      );
      // ==============================================
  
      // --- 評価カテゴリデータのセット例（お好みで既存の内容に合わせて） ---

  
      // 応募者・面接官情報、評価結果は親コンポーネントで設定されているため、ここでは設定しない
      // 子アカウントの保存完了状態のみ設定
      if (isChildAccount && checkSheet.is_completed) {
        setIsChildAccountSaved(true);
      }
  
      // 状態を保存（任意）
      const stateToSave = {
        evaluationCategories: categories,
        currentStage: typeof checkSheet.interview_stage_order === "number"
          ? checkSheet.interview_stage_order - 1
          : 0,
        // 親アカウントの場合のみ評価完了状態を保存
        isInterviewCompleted: !isChildAccount && checkSheet.evaluation && checkSheet.is_completed,
        // 子アカウントの場合は保存完了状態を保存
        isChildAccountSaved: isChildAccount && checkSheet.is_completed,
        evaluationResult: checkSheet.evaluation,
        interviewStages: Array.from(
          { length: checkSheet.total_stages || 1 },
          (_, i) =>
            i === (checkSheet.total_stages - 1)
              ? '最終面接'
              : `${i + 1}次面接`
        )
      };
      saveStateToLocalStorage(stateToSave);

      // 評価完了している場合は子アカウントの回答も取得（親アカウントのみ）
      if (!isChildAccount && checkSheet.evaluation && checkSheet.is_completed) {
        fetchChildAccountsAnswers(checkSheetId);
      }

      setEvaluatingInterview(false);
      setEvaluationProgress('');
      setHistoryLoading(false);
    } catch (error) {
      setEvaluatingInterview(false);
      setEvaluationProgress('');
      setHistoryLoading(false);
      console.error('チェックシート詳細取得エラー:', error);
    }
  };

  // onShowHistoryをhandleShowHistoryにリダイレクト
  const onShowHistory = (checkSheetId) => {
    handleShowHistory(checkSheetId);
  };

  // 面接完了ボタンのクリックハンドラー
  const handleCompleteInterview = async () => {
    const { isValid, errorMessages } = validateForms();
    
    if (!isValid) {
      setSnackbar({
        open: true,
        message: errorMessages.join('、'),
        severity: 'error'
      });
      return;
    }

    // 子アカウントの場合は評価画面に進まず、チェックシート結果のみ保存
    if (isChildAccount) {
      await handleCompleteInterviewChildAccount();
      return;
    }

    // 親アカウントの場合は子アカウントの完了状況を確認
    await handleCompleteInterviewParentAccount();
  };

  // 子アカウント用の面接完了処理
  const handleCompleteInterviewChildAccount = async () => {
    try {
      setEvaluatingInterview(true);
      setEvaluationProgress('面接チェックシートを保存しています...');
      setBrushupLoading(true);

      // 回答データを保存
      const evaluationData = checkSheetRef.current?.getAndApplyEvaluationData();
      if (!evaluationData) {
        alert('評価データの取得に失敗しました。ページを再読み込みしてください。');
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/interview_assistant/bulk-answers/`,
        {
          check_sheet_id: currentCheckSheetId,
          answers: evaluationData.answers,
          candidate_info: {
            name: applicantInfo.name,
            name_kana: applicantInfo.nameKana,
            desired_position: applicantInfo.desiredPosition
          },
          interviewer_info: {
            name: interviewerInfo.name,
            department: interviewerInfo.department,
            position: interviewerInfo.position
          }
        },
        {
          headers: {
            'Content-Type': 'application/json',
            
          },
          withCredentials: true
        }
      );

      // 子アカウント用の面接完了API呼び出し
      const completeResponse = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/interview_assistant/check-sheets/${currentCheckSheetId}/complete-child`,
        {
          candidateInfo: applicantInfo,
          interviewerInfo: interviewerInfo
        },
        {
          headers: {
            'Content-Type': 'application/json',
            
          },
          withCredentials: true
        }
      );

      const result = completeResponse.data;

      // 子アカウント保存完了状態を設定
      setIsChildAccountSaved(true);

      setEvaluatingInterview(false);
      setBrushupLoading(false);
      setSnackbar({
        open: true,
        message: '面接チェックシートが保存されました',
        severity: 'success'
      });

      // 保存完了メッセージ表示後、少し待ってからスクロール
      setTimeout(() => {
        childAccountSavedRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);

      // 子アカウントの場合は3秒後に一覧に戻る
      if (onBackToCheckSheetList) {
        setTimeout(() => {
          onBackToCheckSheetList();
        }, 3000);
      }

    } catch (error) {
      setEvaluatingInterview(false);
      setBrushupLoading(false);
      setSnackbar({
        open: true,
        message: `面接完了処理に失敗しました: ${error.message}`,
        severity: 'error'
      });
    }
  };

  // 親アカウント用の面接完了処理
  const handleCompleteInterviewParentAccount = async () => {
    try {
      // 子アカウントの完了状況を確認
      const childStatusResponse = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/interview_assistant/check-sheets/${currentCheckSheetId}/child-accounts-status`,
        {
          headers: {
            
          },
          withCredentials: true
        }
      );

      const childStatus = childStatusResponse.data;
      
      if (childStatus.total_child_accounts > 0) {
        // 子アカウントの完了状況をダイアログで表示
        const completedCount = childStatus.completed_child_accounts;
        const totalCount = childStatus.total_child_accounts;
        
        // ダイアログを表示
        setChildAccountDialog({
          open: true,
          childAccounts: childStatus.child_accounts || [],
          completedCount,
          totalCount
        });
        
        return; // ダイアログでユーザーの選択を待つ
      }

      // 子アカウントがない場合は直接処理を実行
      await handleCompleteInterviewParentAccountProcess();

    } catch (error) {
      console.error('子アカウント状況確認エラー:', error);
      // エラーが発生した場合は既存処理を続行
      await handleCompleteInterviewParentAccountProcess();
    }
  };

  // 子アカウントダイアログからの続行処理
  const handleProceedWithChildAccountStatus = async () => {
    setChildAccountDialog(prev => ({ ...prev, open: false }));
    await handleCompleteInterviewParentAccountProcess();
  };

  // 子アカウントダイアログのキャンセル
  const handleCancelChildAccountDialog = () => {
    setChildAccountDialog(prev => ({ ...prev, open: false }));
  };

  // 親アカウントの面接完了処理（既存のロジック）
  const handleCompleteInterviewParentAccountProcess = async () => {
    try {
      // 音声解析のみモードの場合は、チェックシートの評価データは不要
      let evaluationData = null;
      let answersArray = [];

      if (evaluationMode !== 'audio') {
        // 1. チェックシートから評価データを取得（checksheet/bothモードのみ）
        evaluationData = checkSheetRef.current?.getAndApplyEvaluationData();
        if (!evaluationData) {
          showError(null, '評価データの取得に失敗しました\n\nページを再読み込みしてください');
          return;
        }
        answersArray = evaluationData.answers;

        // 2. 入力値を新しいカテゴリに反映
        const newCategories = evaluationCategories.map(category => ({
          ...category,
          items: category.items.map(item => ({
            ...item,
            rating: evaluationData.ratings?.[item.id] || '',
            notes: evaluationData.memos?.[item.id] || ''
          }))
        }));
        setEvaluationCategories(newCategories);

        // ←★ ここが重要！InterviewCheckSheet 側の state も即時更新
        if (checkSheetRef.current) {
          // 面接完了直後に ratings/memos を明示的に渡す
          if (typeof checkSheetRef.current.setRatings === 'function') {
            checkSheetRef.current.setRatings(evaluationData.ratings || {});
          }
          if (typeof checkSheetRef.current.setMemos === 'function') {
            checkSheetRef.current.setMemos(evaluationData.memos || {});
          }
        }
      }

      // 3. 反映を確実に待つ
      setTimeout(async () => {
        // バリデーション（音声解析のみモードの場合はスキップ）
        if (evaluationMode !== 'audio' && !answersArray.length) {
          showError(null, '評価データがありません\n\n少なくとも1つの項目を評価してください');
          setBrushupLoading(false);
          setEvaluatingInterview(false);
          return;
        }

        setEvaluatingInterview(true);
        setEvaluationProgress(evaluationMode === 'audio' ? '音声解析結果から評価を生成しています...' : '面接チェックシートの回答を保存しています');
        setEvaluationStep(1);
        setBrushupLoading(true);

        try {
          // 4. 回答保存（音声解析のみモードの場合はスキップ）
          if (evaluationMode !== 'audio' && answersArray.length > 0) {
            const response = await axios.post(
              `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/interview_assistant/bulk-answers/`,
              {
                check_sheet_id: currentCheckSheetId,
                answers: answersArray,
                candidate_info: {
                  name: applicantInfo.name,
                  name_kana: applicantInfo.nameKana,
                  desired_position: applicantInfo.desiredPosition
                },
                interviewer_info: {
                  name: interviewerInfo.name,
                  department: interviewerInfo.department,
                  position: interviewerInfo.position
                },
                interview_process: {
                  interview_stage: (() => {
                    const stage = Number.isInteger(currentStage) ? currentStage : 0;
                    if (formData.interviewStages && formData.interviewStages[stage]) {
                      return formData.interviewStages[stage];
                    }
                    return `${stage + 1}次面接`;
                  })(),
                  interview_stage_order: (() => {
                    const stage = Number.isInteger(currentStage) ? currentStage : 0;
                    return stage + 1;
                  })(),
                  total_stages: (() => {
                    if (formData.interviewStages && Array.isArray(formData.interviewStages) && formData.interviewStages.length > 0) {
                      return formData.interviewStages.length;
                    }
                    const stage = Number.isInteger(currentStage) ? currentStage : 0;
                    return Math.max(stage + 1, 1);
                  })(),
                  is_final_stage: (() => {
                    if (formData.interviewStages && Array.isArray(formData.interviewStages)) {
                      const stage = Number.isInteger(currentStage) ? currentStage : 0;
                      return stage === formData.interviewStages.length - 1;
                    }
                    return false;
                  })()
                }
              },
              {
                headers: {
                  'Content-Type': 'application/json',

                },
                withCredentials: true
              }
            );
          }

          // 5. 評価生成（evaluation_modeを送信）
          setEvaluationProgress(evaluationMode === 'audio' ? '音声解析結果から評価を生成しています...' : '回答の評価を生成しています...');
          setEvaluationStep(2);

          const evaluationResponse = await axios.post(
            `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/interview_assistant/check-sheets/${currentCheckSheetId}/evaluate/?evaluation_mode=${evaluationMode}`,
            {},
            {
              headers: {
                'Content-Type': 'application/json',

              },
              withCredentials: true
            }
          );

          const evaluationResultData = evaluationResponse.data;

          // 6. 評価結果・ロック
          setEvaluationProgress('評価が完了しました');
          setEvaluationStep(3);
          setIsInterviewCompleted(true);
          setEvaluationResult(evaluationResultData);

          // 7. 子アカウントの回答を取得（親アカウントの場合のみ）
          if (!isChildAccount) {
            fetchChildAccountsAnswers(currentCheckSheetId);
          }

          setEvaluatingInterview(false);
          setBrushupLoading(false);

          setSnackbar({
            open: true,
            message: '面接評価が完了しました',
            severity: 'success'
          });

          // 評価結果表示後、少し待ってからスクロール
          setTimeout(() => {
            evaluationResultRef.current?.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }, 100);

        } catch (error) {
          setSnackbar({
            open: true,
            message: `評価の生成・保存でエラー: ${error.message}`,
            severity: 'error'
          });
          setEvaluatingInterview(false);
          setBrushupLoading(false);
        }
      }, 0);

    } catch (error) {
      setSnackbar({
        open: true,
        message: `回答の保存に失敗しました: ${error.message}`,
        severity: 'error'
      });
      setEvaluatingInterview(false);
      setBrushupLoading(false);
    }
  };

  // 次の面接に進む
  const handleNextInterview = async () => {
    setNextStageLoading(true);
    setEvaluatingInterview(true);
    setEvaluationProgress('現在の面接を完了しています...');

    try {
      // 音声解析のみモードの場合は、チェックシートの評価データは不要
      let evaluationData = null;

      if (evaluationMode !== 'audio') {
        // 1. 現在の面接の評価データを取得（checksheet/bothモードのみ）
        evaluationData = checkSheetRef.current?.getAndApplyEvaluationData();
        if (!evaluationData || evaluationData.answers.length === 0) {
          setSnackbar({
            open: true,
            message: '評価データが不足しています。少なくとも1つの項目を評価してから次の面接に進んでください。',
            severity: 'error'
          });
          setNextStageLoading(false);
          setEvaluatingInterview(false);
          return;
        }

        // 2. 現在の面接の回答を保存
        setEvaluationProgress('現在の面接の回答を保存しています...');
        await axios.post(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/interview_assistant/bulk-answers/`,
          {
            check_sheet_id: currentCheckSheetId,
            answers: evaluationData.answers,
            candidate_info: {
              name: applicantInfo.name,
              name_kana: applicantInfo.nameKana,
              desired_position: applicantInfo.desiredPosition
            },
            interviewer_info: {
              name: interviewerInfo.name,
              department: interviewerInfo.department,
              position: interviewerInfo.position
            }
          },
          {
            headers: {
              'Content-Type': 'application/json',

            },
            withCredentials: true
          }
        );
      } else {
        // 音声解析のみモードの場合
        setEvaluationProgress('音声解析結果から評価を生成しています...');
      }

      // 3. 現在の面接を評価して完了（evaluation_modeを送信）
      setEvaluationProgress('現在の面接を評価しています...');
      const evaluationResponse = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/interview_assistant/check-sheets/${currentCheckSheetId}/evaluate/?evaluation_mode=${evaluationMode}`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',

          },
          withCredentials: true
        }
      );

      // 4. 次の面接チェックシートを作成
      setEvaluationProgress('次の面接チェックシートを作成しています...');
      const nextInterviewResponse = await interviewAPI.nextInterview(currentCheckSheetId);
      
      // 5. チェックシートIDを更新
      setCurrentCheckSheetId(nextInterviewResponse.data.id);
      
      // 6. フォームデータを更新
      setFormData(prev => ({
        ...prev,
        interview_stage: nextInterviewResponse.data.interview_stage,
        interview_stage_order: nextInterviewResponse.data.interview_stage_order,
        total_stages: nextInterviewResponse.data.total_stages,
        is_final_stage: nextInterviewResponse.data.is_final_stage,
        candidateInfo: nextInterviewResponse.data.candidate_name || nextInterviewResponse.data.candidate_info?.name,
        position: nextInterviewResponse.data.position,
        interviewer: nextInterviewResponse.data.interviewer,
        decision_status: null // 次の面接では採用状態をリセット
      }));

      // 7. 質問を取得して評価カテゴリを更新
      let newCategories = [];
      const questionsResponse = await interviewAPI.getQuestions(nextInterviewResponse.data.id);
      if (questionsResponse.data && questionsResponse.data.length > 0) {
        const questionsByCategory = questionsResponse.data.reduce((acc, question) => {
          if (!acc[question.category]) {
            acc[question.category] = [];
          }
          acc[question.category].push({
            id: `q_${question.id}`,
            text: question.question,
            highlighted: question.is_required,
            questionId: question.id
          });
          return acc;
        }, {});

        newCategories = Object.entries(questionsByCategory).map(([name, items]) => ({
          name,
          items
        }));

        setEvaluationCategories(newCategories);
      }

      // 8. 面接完了状態をリセット
      setIsInterviewCompleted(false);
      setEvaluationResult(null);

      // 9. メッセージを更新
      setMessages([
        {
          sender: 'bot',
          text: `前回の面接が完了しました。${nextInterviewResponse.data.interview_stage}のチェックシートを作成しました。`
        },
        {
          sender: 'bot',
          text: '前回の評価結果を踏まえた新しい質問を生成しています。新しい面接を開始してください。'
        }
      ]);

      // 10. 状態を保存
      saveStateToLocalStorage({
        currentCheckSheetId: nextInterviewResponse.data.id,
        formData: {
          ...formData,
          interview_stage: nextInterviewResponse.data.interview_stage,
          interview_stage_order: nextInterviewResponse.data.interview_stage_order,
          total_stages: nextInterviewResponse.data.total_stages,
          is_final_stage: nextInterviewResponse.data.is_final_stage,
          candidateInfo: nextInterviewResponse.data.candidate_name || nextInterviewResponse.data.candidate_info?.name,
          position: nextInterviewResponse.data.position,
          interviewer: nextInterviewResponse.data.interviewer,
          decision_status: null
        },
        isInterviewCompleted: false,
        evaluationResult: null,
        evaluationCategories: newCategories
      });

      setSnackbar({
        open: true,
        message: `${nextInterviewResponse.data.interview_stage}に進みました`,
        severity: 'success'
      });

      // 次の面接に進んだ後、ページトップにスクロール
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }, 100);

    } catch (error) {
      console.error("次の面接への移行エラー:", error);
      setSnackbar({
        open: true,
        message: `次の面接への移行中にエラーが発生しました: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setNextStageLoading(false);
      setEvaluatingInterview(false);
      setEvaluationProgress('');
    }
  };

  // 子アカウントの回答を取得する関数
  const fetchChildAccountsAnswers = async (checkSheetId) => {
    if (!checkSheetId) return;

    setLoadingChildAnswers(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/interview_assistant/check-sheets/${checkSheetId}/child-accounts-answers`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
          withCredentials: true
        }
      );
      setChildAccountsAnswers(response.data);
    } catch (error) {
      console.error('子アカウントの回答取得に失敗:', error);
      // エラーでもUIは動作させる
    } finally {
      setLoadingChildAnswers(false);
    }
  };

  // InterviewCheckSheetからデータを受け取るコールバック
  const handleSaveAnswers = (answers, ratings, memos, questionIds = {}) => {
    // ステートを更新（ローカルストレージへの保存はInterviewCheckSheetコンポーネント内で行う）
    setInterviewCheckSheetState({
      ratings,
      memos,
      questionIds, // 質問IDも保存
      lastSaved: new Date().toISOString()
    });
    
  };

  // 評価結果を設定する関数を追加
  const setEvaluationResultAndState = (result) => {
    setEvaluationResult(result);
    setIsInterviewCompleted(true);
    // 評価結果をローカルストレージにも保存
    saveStateToLocalStorage({
      evaluationResult: result,
      isInterviewCompleted: true
    });
  };

  // currentCheckSheetId が変わった時は親コンポーネント側で処理するため、
  // ここでの自動読み込みは削除（二重読み込みを防止）

  // formDataから現在のステージを設定
  useEffect(() => {
    if (formData.interview_stage_order !== undefined) {
      const newCurrentStage = formData.interview_stage_order - 1;
      setCurrentStage(newCurrentStage);
    }
  }, [formData.interview_stage_order, formData.interview_stage]);

  // 採用/不採用ステータス更新関数を追加
  const handleUpdateDecisionStatus = async (status) => {
    try {
      setBrushupLoading(true);
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/interview_assistant/check-sheets/${currentCheckSheetId}/decision`,
        { 
          decision_status: formData.decision_status === status ? null : status 
        },
        {
          headers: {
            
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );

      const result = response.data;

      // 成功メッセージを表示
      setSnackbar({
        open: true,
        message: result.decision_status === 'hired' 
          ? '採用に決定しました' 
          : result.decision_status === 'rejected'
          ? '不採用に決定しました'
          : 'ステータスを取り消しました',
        severity: 'success'
      });

      // ローカルステートを更新
      setFormData(prev => ({
        ...prev,
        decision_status: result.decision_status
      }));

      // 親コンポーネントのステート更新用関数を呼び出す
      saveStateToLocalStorage({
        decision_status: result.decision_status
      });

    } catch (error) {
      console.error('Error updating decision status:', error);
      setSnackbar({
        open: true,
        message: 'ステータスの更新に失敗しました',
        severity: 'error'
      });
    } finally {
      setBrushupLoading(false);
    }
  };

  // 採用ボタンのクリックハンドラー
  const handleHire = () => {
    handleUpdateDecisionStatus('hired');
  };

  // 不採用ボタンのクリックハンドラー
  const handleReject = () => {
    handleUpdateDecisionStatus('rejected');
  };

  // 採用状態の判定
  const isHired = formData.decision_status === 'hired';
  const isRejected = formData.decision_status === 'rejected';
  
  if (nextStageLoading) {
    return <LoadingScreen message="次の面接準備中..." loadingType="interview_next_stage" featureKey="interview_support" />;
  }

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      sx={{
        flexGrow: 1,
        overflow: 'auto',
        py: 3,
        px: { xs: 2, md: 3 },
        backgroundColor: bg(theme).card,
        maxWidth: 1200,
        mx: 'auto'
      }}
    >
      {/* 評価中のローディングオーバーレイ */}
      {evaluatingInterview && (
        <LoadingScreen
          variant="overlay"
          message={evaluationProgress || '面接評価を生成中...'}
          description="AIが回答データを分析しています。しばらくお待ちください..."
          loadingType="interview_evaluate"
          featureKey="interview_support"
        />
      )}

      {/* 評価モード選択 */}
      {!isInterviewCompleted && !isChildAccountSaved && (
        <Paper
          elevation={2}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: 2,
            background: gradient(theme).subtle,
            border: `1px solid ${border(theme).default}`
          }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: textColors(theme).primary }}>
                評価方法
              </Typography>
              <Tooltip title="評価方法はいつでも変更できます。切り替えても入力済みのデータは保持されます。">
                <InfoIcon fontSize="small" sx={{ color: textColors(theme).muted, cursor: 'help' }} />
              </Tooltip>
            </Box>

            <ToggleButtonGroup
              value={evaluationMode}
              exclusive
              onChange={handleEvaluationModeChange}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  fontWeight: 500,
                  borderColor: border(theme).default,
                  '&.Mui-selected': {
                    backgroundColor: '#1976d2',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#1565c0'
                    }
                  }
                }
              }}
            >
              <ToggleButton value="checksheet">
                <AssignmentIcon sx={{ mr: 0.5, fontSize: 18 }} />
                チェックシート
              </ToggleButton>
              <ToggleButton value="audio">
                <MicIcon sx={{ mr: 0.5, fontSize: 18 }} />
                音声解析
              </ToggleButton>
              <ToggleButton value="both">
                <PlaylistAddCheckIcon sx={{ mr: 0.5, fontSize: 18 }} />
                両方（推奨）
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* 評価モードの説明 */}
          <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: textColors(theme).secondary }}>
            {evaluationMode === 'checksheet' && '面接官がチェックシートで手動評価を行います。'}
            {evaluationMode === 'audio' && '録音音声をAIが自動解析して評価します。'}
            {evaluationMode === 'both' && 'チェックシート評価と音声解析を組み合わせて、より精度の高い総合評価を行います。'}
          </Typography>

          {/* 進捗サマリー */}
          {primaryAction?.showProgress && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="body2" sx={{ color: textColors(theme).secondary }}>
                  評価進捗
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                  {checkSheetProgress.completed}/{checkSheetProgress.total} 完了
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={checkSheetProgress.percentage}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: border(theme).light,
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    backgroundColor: checkSheetProgress.percentage === 100 ? '#22c55e' : '#1976d2'
                  }
                }}
              />
            </Box>
          )}
        </Paper>
      )}

      {/* 面接ステージの表示 */}
      {formData.interviewStages && formData.interviewStages.length > 0 && (
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, md: 3 },
            mb: 4,
            borderRadius: 2,
            background: gradient(theme).subtle,
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
            overflow: 'hidden'
          }}
        >
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: 'text.primary', textAlign: 'center' }}>
            面接プロセス
          </Typography>
          
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'stretch',
            justifyContent: 'center',
            position: 'relative',
            mb: 3,
            gap: { xs: 2, md: 2 }
          }}>
            {formData.interviewStages.map((stage, index) => {
              const isActive = index === currentStage;
              const isCompleted = index < currentStage;

              return (
                <React.Fragment key={index}>
                  <Box
                    component={motion.div}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.4,
                      delay: index * 0.1,
                      ease: [0.4, 0, 0.2, 1]
                    }}
                    sx={{
                      flex: 1,
                      minWidth: { xs: '100%', md: '140px' },
                      position: 'relative'
                    }}
                  >
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        border: '2px solid',
                        borderColor: isActive
                          ? 'primary.main'
                          : isCompleted
                          ? 'success.main'
                          : border(theme).default,
                        background: isActive
                          ? 'linear-gradient(135deg, rgba(33, 150, 243, 0.1), rgba(66, 165, 245, 0.05))'
                          : isCompleted
                          ? 'linear-gradient(135deg, rgba(76, 175, 80, 0.08), rgba(129, 199, 132, 0.05))'
                          : gradient(theme).card,
                        boxShadow: isActive
                          ? '0 4px 16px rgba(33, 150, 243, 0.2)'
                          : isCompleted
                          ? '0 2px 8px rgba(76, 175, 80, 0.12)'
                          : '0 1px 4px rgba(0,0,0,0.06)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          boxShadow: isActive
                            ? '0 6px 20px rgba(33, 150, 243, 0.25)'
                            : isCompleted
                            ? '0 4px 12px rgba(76, 175, 80, 0.18)'
                            : '0 2px 8px rgba(0,0,0,0.1)',
                          transform: 'translateY(-2px)'
                        }
                      }}
                    >
                      {/* ステップ番号とアイコン */}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: isActive
                              ? 'linear-gradient(135deg, #2196f3, #42a5f5)'
                              : isCompleted
                              ? 'linear-gradient(135deg, #4caf50, #81c784)'
                              : `linear-gradient(135deg, ${border(theme).default}, ${bg(theme).tinted})`,
                            color: isActive || isCompleted ? '#ffffff' : '#9e9e9e',
                            fontWeight: 700,
                            fontSize: '1.1rem',
                            boxShadow: isActive
                              ? '0 2px 8px rgba(33, 150, 243, 0.3)'
                              : isCompleted
                              ? '0 2px 8px rgba(76, 175, 80, 0.25)'
                              : 'none',
                            position: 'relative'
                          }}
                        >
                          {isCompleted ? (
                            <CheckCircleIcon sx={{ fontSize: 24 }} />
                          ) : (
                            index + 1
                          )}
                          {isActive && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                borderRadius: '50%',
                                border: '2px solid rgba(33, 150, 243, 0.3)',
                                animation: 'pulse 2s infinite'
                              }}
                            />
                          )}
                        </Box>
                      </Box>

                      {/* ステージ名 */}
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: isActive ? 700 : isCompleted ? 600 : 500,
                          color: isActive
                            ? 'primary.main'
                            : isCompleted
                            ? 'success.main'
                            : 'text.secondary',
                          textAlign: 'center',
                          fontSize: '0.85rem'
                        }}
                      >
                        {stage}
                      </Typography>

                      {/* ステータスラベル */}
                      {(isActive || isCompleted) && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.75 }}>
                          <Chip
                            label={isActive ? '進行中' : '完了'}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              bgcolor: isActive
                                ? alpha('#2196f3', 0.15)
                                : alpha('#4caf50', 0.15),
                              color: isActive ? '#2196f3' : '#4caf50',
                              border: '1px solid',
                              borderColor: isActive
                                ? alpha('#2196f3', 0.3)
                                : alpha('#4caf50', 0.3)
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* コネクタ（矢印） */}
                  {index < formData.interviewStages.length - 1 && (
                    <Box
                      sx={{
                        display: { xs: 'none', md: 'flex' },
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        flexShrink: 0
                      }}
                    >
                      <Box
                        sx={{
                          width: 0,
                          height: 0,
                          borderTop: '12px solid transparent',
                          borderBottom: '12px solid transparent',
                          borderLeft: `16px solid ${
                            index < currentStage ? '#4caf50' : border(theme).default
                          }`,
                          transition: 'border-left-color 0.3s ease'
                        }}
                      />
                    </Box>
                  )}
                </React.Fragment>
              );
            })}
          </Box>

          {/* CSSアニメーション定義 */}
          <style>
            {`
              @keyframes pulse {
                0%, 100% {
                  transform: scale(1);
                  opacity: 1;
                }
                50% {
                  transform: scale(1.1);
                  opacity: 0.7;
                }
              }
            `}
          </style>
          
          {/* 現在のステージ情報 */}
          <Box sx={{ 
            mt: 3, 
            pt: 2, 
            borderTop: '1px solid', 
            borderColor: 'divider',
            textAlign: 'center'
          }}>
            {currentStage > 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                前回の面接: {formData.interviewStages[currentStage - 1]}
              </Typography>
            )}
          </Box>
          {/* 面接プロセス表示部分の最後に採用・不採用ボタンを追加 */}
          {formData.is_final_stage && isInterviewCompleted && !isChildAccount && (
            <>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center',
                gap: 2,
                mt: 3 
              }}>
                <Button
                  variant={isHired ? "outlined" : "contained"}
                  color="success"
                  startIcon={<WorkIcon />}
                  size="large"
                  onClick={handleHire}
                  disabled={evaluatingInterview}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 10,
                    fontWeight: 'bold',
                    color: isHired ? 'inherit' : '#ffffff',
                    boxShadow: isHired ? 'none' : '0 6px 15px rgba(76, 175, 80, 0.3)',
                    background: isHired ? 'none' : 'linear-gradient(45deg, #2e7d32, #4caf50)',
                    minWidth: 200,
                    '&:hover': {
                      background: isHired ? 'none' : 'linear-gradient(45deg, #1b5e20, #388e3c)',
                      boxShadow: isHired ? 'none' : '0 8px 20px rgba(76, 175, 80, 0.4)',
                      transform: 'translateY(-2px)'
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                      boxShadow: isHired ? 'none' : '0 4px 10px rgba(76, 175, 80, 0.2)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  {isHired ? '採用を取り消す' : '採用する'}
                </Button>
                <Button
                  variant={isRejected ? "outlined" : "contained"}
                  color="error"
                  startIcon={<BlockIcon />}
                  size="large"
                  onClick={handleReject}
                  disabled={evaluatingInterview}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 10,
                    fontWeight: 'bold',
                    boxShadow: isRejected ? 'none' : '0 6px 15px rgba(211, 47, 47, 0.3)',
                    background: isRejected ? 'none' : 'linear-gradient(45deg, #d32f2f, #f44336)',
                    minWidth: 200,
                    '&:hover': {
                      background: isRejected ? 'none' : 'linear-gradient(45deg, #b71c1c, #d32f2f)',
                      boxShadow: isRejected ? 'none' : '0 8px 20px rgba(211, 47, 47, 0.4)',
                      transform: 'translateY(-2px)'
                    },
                    '&:active': {
                      transform: 'translateY(0px)',
                      boxShadow: isRejected ? 'none' : '0 4px 10px rgba(211, 47, 47, 0.2)'
                    },
                    transition: 'all 0.2s ease'
                  }}
                >
                  {isRejected ? '不採用を取り消す' : '不採用にする'}
                </Button>
              </Box>
              {isHired && (
                <Typography color="success.main" sx={{ mt: 2, textAlign: 'center', fontWeight: 'bold' }}>
                  この応募者は採用が決定しました
                </Typography>
              )}
              {isRejected && (
                <Typography color="error" sx={{ mt: 2, textAlign: 'center', fontWeight: 'bold' }}>
                  この応募者は不採用になりました
                </Typography>
              )}
            </>
          )}

          {/* 次の面接に進むボタンと不採用ボタンを並べて表示 */}
          {formData.interviewStages && formData.interviewStages.length > currentStage + 1 && !isChildAccount && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 2,
                mt: 3,
                mb: 4
              }}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={<NavigateNextIcon />}
                size="large"
                onClick={handleNextInterview}
                disabled={evaluatingInterview || isRejected || nextStageLoading}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 10,
                  fontWeight: 'bold',
                  boxShadow: '0 6px 15px rgba(33, 150, 243, 0.3)',
                  background: 'linear-gradient(45deg, #1976d2, #42a5f5)',
                  minWidth: 200,
                  '&:hover': {
                    background: 'linear-gradient(45deg, #1565c0, #1976d2)',
                    boxShadow: '0 8px 20px rgba(33, 150, 243, 0.4)',
                    transform: 'translateY(-2px)'
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                    boxShadow: '0 4px 10px rgba(33, 150, 243, 0.2)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                {formData.interviewStages && formData.interviewStages[currentStage]
                  ? `${formData.interviewStages[currentStage]}合格`
                  : '面接合格'}
              </Button>
              <Button
                variant={isRejected ? "outlined" : "contained"}
                color="error"
                startIcon={<BlockIcon />}
                size="large"
                onClick={handleReject}
                disabled={evaluatingInterview || isHired}
                sx={{
                  px: 4,
                  py: 1.5,
                  borderRadius: 10,
                  fontWeight: 'bold',
                  boxShadow: isRejected ? 'none' : '0 6px 15px rgba(211, 47, 47, 0.3)',
                  background: isRejected ? 'none' : 'linear-gradient(45deg, #d32f2f, #f44336)',
                  minWidth: 200,
                  '&:hover': {
                    background: isRejected ? 'none' : 'linear-gradient(45deg, #b71c1c, #d32f2f)',
                    boxShadow: isRejected ? 'none' : '0 8px 20px rgba(211, 47, 47, 0.4)',
                    transform: 'translateY(-2px)'
                  },
                  '&:active': {
                    transform: 'translateY(0px)',
                    boxShadow: isRejected ? 'none' : '0 4px 10px rgba(211, 47, 47, 0.2)'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                {isRejected ? '不採用を取り消す' : '不採用にする'}
              </Button>
            </Box>
          )}
        </Paper>
      )}

      {/* 前回の面接評価結果表示（次の面接段階の場合） */}
      {formData.previousEvaluationResult && currentStage > 0 && !isInterviewCompleted && (
        <Paper
          elevation={3}
          sx={{
            p: { xs: 2, md: 3 },
            mb: 4,
            borderRadius: 2,
            background: adaptiveColor(theme, 'linear-gradient(to right, #f0f8ff, #ffffff)', 'linear-gradient(to right, rgba(33,150,243,0.08), rgba(30,30,30,0.3))'),
            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          }}
        >
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold', color: 'text.primary', textAlign: 'center' }}>
            前回の面接評価結果（{formData.previousFormData?.interviewStages?.[formData.previousStage] || '前回面接'}）
          </Typography>
          
          <EvaluationResult
            evaluationResult={formData.previousEvaluationResult}
            applicantInfo={applicantInfo}
            interviewerInfo={interviewerInfo}
            isReadOnly={true}
            showTitle={false}
          />
        </Paper>
      )}

      {/* 面接完了後の表示 */}
      {isInterviewCompleted || isChildAccountSaved ? (
        <>
          {/* 子アカウントの場合は保存完了メッセージのみ表示 */}
          {isChildAccount && isChildAccountSaved ? (
            <Paper
              ref={childAccountSavedRef}
              elevation={3}
              sx={{
                p: 4,
                mb: 4,
                borderRadius: 3,
                textAlign: 'center',
                background: adaptiveColor(theme, 'linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%)', 'linear-gradient(135deg, rgba(76,175,80,0.12) 0%, rgba(76,175,80,0.06) 100%)'),
                border: '2px solid #4caf50'
              }}
            >
              <CheckCircleOutlineIcon 
                sx={{ fontSize: 64, color: 'success.main', mb: 2 }} 
              />
              <Typography variant="h5" sx={{ mb: 2, fontWeight: 'bold', color: 'success.main' }}>
                面接チェックシートが保存されました
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                面接評価は親アカウントで統合して行われます。
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {onBackToCheckSheetList ? '3秒後にチェックシート一覧に戻ります。' : 'お疲れ様でした。'}
              </Typography>
              {onBackToCheckSheetList && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={onBackToCheckSheetList}
                    startIcon={<NavigateNextIcon sx={{ transform: 'rotate(180deg)' }} />}
                  >
                    すぐに一覧に戻る
                  </Button>
                </Box>
              )}
            </Paper>
                      ) : (
            /* 親アカウントの場合は評価結果を表示 */
            isInterviewCompleted && evaluationResult && (
              <>
                <Box ref={evaluationResultRef}>
                  <EvaluationResult
                    evaluationResult={evaluationResult}
                    applicantInfo={applicantInfo}
                    interviewerInfo={interviewerInfo}
                  />
                </Box>

                {/* 親/子アカウント回答切り替えUI */}
                {childAccountsAnswers && childAccountsAnswers.child_accounts && childAccountsAnswers.child_accounts.length > 0 && (
                  <Paper
                    elevation={2}
                    sx={{
                      p: 3,
                      mb: 3,
                      borderRadius: 2,
                      backgroundColor: bg(theme).subtle,
                      border: `1px solid ${border(theme).light}`
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <GroupIcon sx={{ mr: 1, color: '#249BE0' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: textColors(theme).primary }}>
                        面接官別の回答を表示
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: textColors(theme).secondary, mb: 2 }}>
                      複数の面接官が回答した場合、それぞれの回答を切り替えて確認できます。
                    </Typography>
                    <ToggleButtonGroup
                      value={answersViewMode}
                      exclusive
                      onChange={(e, newMode) => {
                        if (newMode !== null) {
                          setAnswersViewMode(newMode);
                        }
                      }}
                      sx={{
                        flexWrap: 'wrap',
                        gap: 1,
                        '& .MuiToggleButton-root': {
                          borderRadius: '8px !important',
                          border: `1px solid ${border(theme).light} !important`,
                          px: 2,
                          py: 1,
                          '&.Mui-selected': {
                            backgroundColor: '#249BE0',
                            color: 'white',
                            '&:hover': {
                              backgroundColor: '#1e8bc8',
                            }
                          }
                        }
                      }}
                    >
                      <ToggleButton value="parent">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon sx={{ fontSize: 18 }} />
                          <Box sx={{ textAlign: 'left' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              親アカウント
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.8 }}>
                              {childAccountsAnswers.parent_account?.answers_count || 0}件の回答
                            </Typography>
                          </Box>
                        </Box>
                      </ToggleButton>
                      {childAccountsAnswers.child_accounts.map((child, index) => (
                        <ToggleButton key={child.child_user_id} value={`child_${index}`}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <SupervisorAccountIcon sx={{ fontSize: 18 }} />
                            <Box sx={{ textAlign: 'left' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {child.email.split('@')[0]}
                              </Typography>
                              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                {child.answers_count}件の回答
                              </Typography>
                            </Box>
                          </Box>
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Paper>
                )}

                {/* チェックシート */}
                <InterviewCheckSheet
                  ref={checkSheetRef}
                  evaluationCategories={evaluationCategories}
                  setEvaluationCategories={setEvaluationCategories}
                  checkSheetId={currentCheckSheetId}
                  evaluationResult={null} // 評価結果はここでは表示しない
                  onSaveAnswers={handleSaveAnswers}
                  isReadOnly={isInterviewCompleted}
                  initialApplicantInfo={applicantInfo}
                  initialInterviewerInfo={interviewerInfo}
                  showEvaluation={false} // 評価結果はMainContentで表示するため、ここでは表示しない
                  formData={formData}
                  currentStage={currentStage}
                  handleNextInterview={handleNextInterview}
                  isInterviewCompleted
                  viewingAnswers={
                    answersViewMode === 'parent'
                      ? null // 親アカウントの場合はデフォルト（評価カテゴリから表示）
                      : childAccountsAnswers?.child_accounts?.[parseInt(answersViewMode.replace('child_', ''))]?.answers || null
                  }
                  viewingAccountLabel={
                    answersViewMode === 'parent'
                      ? null
                      : childAccountsAnswers?.child_accounts?.[parseInt(answersViewMode.replace('child_', ''))]?.email || null
                  }
                />
              </>
            )
          )}
        </>
      ) : (
        <>
          {/* 応募者と面接者の情報入力 */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* 応募者情報 */}
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="h6">応募者情報</Typography>
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        label="氏名"
                        variant="outlined"
                        size="small"
                        value={applicantInfo.name}
                        onChange={(e) => setApplicantInfo(prev => ({ ...prev, name: e.target.value }))}
                        error={!!errors.applicantName}
                        helperText={isChildAccount ? "親アカウントが管理する情報です（読み取り専用）" : errors.applicantName}
                        disabled={isChildAccount}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        label="フリガナ"
                        variant="outlined"
                        size="small"
                        value={applicantInfo.nameKana}
                        onChange={(e) => setApplicantInfo(prev => ({ ...prev, nameKana: e.target.value }))}
                        error={!!errors.applicantNameKana}
                        helperText={isChildAccount ? "親アカウントが管理する情報です（読み取り専用）" : errors.applicantNameKana}
                        disabled={isChildAccount}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        label="希望の役職"
                        variant="outlined"
                        size="small"
                        value={applicantInfo.desiredPosition}
                        onChange={(e) => setApplicantInfo(prev => ({ ...prev, desiredPosition: e.target.value }))}
                        error={!!errors.applicantDesiredPosition}
                        helperText={isChildAccount ? "親アカウントが管理する情報です（読み取り専用）" : errors.applicantDesiredPosition}
                        disabled={isChildAccount}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="特記事項"
                        variant="outlined"
                        size="small"
                        multiline
                        rows={2}
                        value={applicantInfo.notes}
                        onChange={(e) => setApplicantInfo(prev => ({ ...prev, notes: e.target.value }))}
                        helperText={isChildAccount ? "親アカウントが管理する情報です（読み取り専用）" : ""}
                        disabled={isChildAccount}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
            
            {/* 面接者情報 */}
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <SupervisorAccountIcon sx={{ mr: 1, color: 'secondary.main' }} />
                    <Typography variant="h6">面接者情報</Typography>
                  </Box>
                  
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        label="氏名"
                        variant="outlined"
                        size="small"
                        value={interviewerInfo.name}
                        onChange={(e) => setInterviewerInfo(prev => ({ ...prev, name: e.target.value }))}
                        error={!!errors.interviewerName}
                        helperText={isChildAccount ? "面接を実施する方の氏名を入力してください" : errors.interviewerName}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        label="所属部署"
                        variant="outlined"
                        size="small"
                        value={interviewerInfo.department}
                        onChange={(e) => setInterviewerInfo(prev => ({ ...prev, department: e.target.value }))}
                        error={!!errors.interviewerDepartment}
                        helperText={isChildAccount ? "面接を実施する方の所属部署を入力してください" : errors.interviewerDepartment}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        label="役職"
                        variant="outlined"
                        size="small"
                        value={interviewerInfo.position}
                        onChange={(e) => setInterviewerInfo(prev => ({ ...prev, position: e.target.value }))}
                        error={!!errors.interviewerPosition}
                        helperText={isChildAccount ? "面接を実施する方の役職を入力してください" : errors.interviewerPosition}
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* 音声録音・アップロードパネル（評価モードが'audio'または'both'の場合のみ表示） */}
          {(evaluationMode === 'audio' || evaluationMode === 'both') && (
            <Paper
              elevation={3}
              sx={{
                p: 3,
                mb: 4,
                borderRadius: 3,
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                background: gradient(theme).subtle
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <MicIcon sx={{ color: '#1976d2' }} />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: textColors(theme).primary }}>
                  面接音声の録音・アップロード
                </Typography>
                {evaluationMode === 'both' && (
                  <Chip label="任意" size="small" variant="outlined" sx={{ ml: 1 }} />
                )}
              </Box>
              <Typography variant="body2" sx={{ mb: 3, color: textColors(theme).secondary }}>
                面接の音声を録音またはアップロードして、AIによる解析を行うことができます。
              </Typography>
              <AudioRecorderPanel
                checkSheetId={currentCheckSheetId}
                audioService={audioService}
                disabled={isInterviewCompleted}
                onRecordingComplete={(response) => {
                  setSnackbar({
                    open: true,
                    message: '録音データのアップロードが完了しました',
                    severity: 'success'
                  });
                }}
                onUploadComplete={(response) => {
                  setSnackbar({
                    open: true,
                    message: '音声ファイルのアップロードが完了しました',
                    severity: 'success'
                  });
                }}
              />
            </Paper>
          )}

          {/* 面接アシスト拡張機能 */}
          {currentCheckSheetId && (
            <Box sx={{ mb: 4 }}>
              {/* バイアス検出パネル */}
              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    バイアス検出分析
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <BiasAnalysisPanel
                    checkSheetId={currentCheckSheetId}
                    questions={evaluationCategories?.flatMap(cat => cat.items.map(item => item.text)) || []}
                  />
                </AccordionDetails>
              </Accordion>
            </Box>
          )}

          {/* 面接履歴表示 */}
          {interviewHistory.length > 0 && !isInterviewCompleted && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                過去の面接履歴
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                {interviewHistory.map((interview, index) => (
                  <Accordion key={index} elevation={1} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography sx={{ fontWeight: 'medium' }}>
                        {interview.stageName} ({new Date(interview.date).toLocaleDateString()})
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" color="primary">応募者:</Typography>
                          <Typography variant="body2">{interview.applicant.name}</Typography>
                          <Typography variant="body2">{interview.applicant.position}</Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" color="secondary">面接担当者:</Typography>
                          <Typography variant="body2">{interview.interviewer.name}</Typography>
                          <Typography variant="body2">{interview.interviewer.department}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ mt: 1 }}>評価カテゴリ:</Typography>
                          <Box component="ul" sx={{ pl: 2 }}>
                            {interview.evaluationData.map((cat, catIdx) => (
                              <Box component="li" key={catIdx}>
                                {cat.name} ({cat.items.length}項目)
                              </Box>
                            ))}
                          </Box>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Box>
          )}

          {/* チェックシート（評価モードが'checksheet'または'both'の場合のみ表示） */}
          {(evaluationMode === 'checksheet' || evaluationMode === 'both') && (
            <>
              <InterviewCheckSheet
                ref={checkSheetRef}
                evaluationCategories={evaluationCategories}
                setEvaluationCategories={setEvaluationCategories}
                checkSheetId={currentCheckSheetId}
                evaluationResult={null} // 評価結果はここでは表示しない
                onSaveAnswers={handleSaveAnswers}
                isReadOnly={isInterviewCompleted}
                initialApplicantInfo={applicantInfo}
                initialInterviewerInfo={interviewerInfo}
                showEvaluation={false} // 評価結果はMainContentで表示するため、ここでは表示しない
                formData={formData}
                currentStage={currentStage}
                handleNextInterview={handleNextInterview}
                isInterviewCompleted
                // 項目追加用props
                categoryNameInput={categoryNameInput}
                setCategoryNameInput={setCategoryNameInput}
                handleAddCategory={handleAddCategory}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                itemTextInput={itemTextInput}
                setItemTextInput={setItemTextInput}
                handleAddItem={handleAddItem}
                showAddSection={!isInterviewCompleted}
              />

            </>
          )}

          {/* 単一CTA（Primary Action）ボタン */}
          {!isInterviewCompleted && !isChildAccountSaved && primaryAction && (
            <Paper
              elevation={3}
              sx={{
                mt: 4,
                p: 3,
                borderRadius: 3,
                background: gradient(theme).subtle,
                border: `1px solid ${border(theme).light}`,
                textAlign: 'center'
              }}
            >
              {/* 子アカウント用の案内メッセージ */}
              {isChildAccount && (
                <Box sx={{ mb: 2, p: 1.5, backgroundColor: statusBg(theme).info, borderRadius: 2 }}>
                  <Typography variant="body2" color="primary">
                    <PersonIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: 18 }} />
                    子アカウントモード：面接チェックシートの保存のみ行われます
                  </Typography>
                </Box>
              )}

              {/* 次のステップの説明 */}
              {primaryAction.description && (
                <Typography variant="body2" sx={{ mb: 2, color: textColors(theme).secondary }}>
                  {primaryAction.description}
                </Typography>
              )}

              <Button
                variant={primaryAction.variant || 'contained'}
                color={primaryAction.color || 'primary'}
                startIcon={evaluatingInterview ? <CircularProgress size={24} color="inherit" /> : <CheckCircleOutlineIcon />}
                size="large"
                sx={{
                  py: 1.5,
                  px: 5,
                  borderRadius: 3,
                  fontWeight: 'bold',
                  fontSize: '1.1rem',
                  boxShadow: primaryAction.color === 'success'
                    ? '0 4px 14px rgba(34, 197, 94, 0.4)'
                    : '0 4px 14px rgba(25, 118, 210, 0.4)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: primaryAction.color === 'success'
                      ? '0 6px 20px rgba(34, 197, 94, 0.5)'
                      : '0 6px 20px rgba(25, 118, 210, 0.5)',
                  },
                  transition: 'all 0.2s ease'
                }}
                onClick={handleCompleteInterview}
                disabled={brushupLoading || evaluatingInterview}
              >
                {evaluatingInterview
                  ? (isChildAccount ? 'チェックシート保存中...' : '評価中...')
                  : brushupLoading
                    ? '面接評価中...'
                    : primaryAction.label
                }
              </Button>

              {/* エラーメッセージ表示 */}
              {Object.keys(errors).length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="error" sx={{ textAlign: 'left' }}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                      以下の項目を入力してください：
                    </Typography>
                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                      {Object.values(errors).map((error, index) => (
                        <Box component="li" key={index}>
                          {error}
                        </Box>
                      ))}
                    </Box>
                  </Alert>
                </Box>
              )}
            </Paper>
          )}
        </>
      )}

      {/* 子アカウント完了状況確認ダイアログ */}
      <Dialog
        open={childAccountDialog.open}
        onClose={handleCancelChildAccountDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            textAlign: 'center'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <GroupIcon sx={{ fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              子アカウント面接状況確認
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          {/* 完了状況サマリー */}
          <Paper
            elevation={2}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              background: childAccountDialog.completedCount === childAccountDialog.totalCount
                ? adaptiveColor(theme, 'linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%)', 'linear-gradient(135deg, rgba(76,175,80,0.12) 0%, rgba(76,175,80,0.06) 100%)')
                : childAccountDialog.completedCount === 0
                  ? adaptiveColor(theme, 'linear-gradient(135deg, #ffebee 0%, #fce4ec 100%)', 'linear-gradient(135deg, rgba(244,67,54,0.12) 0%, rgba(244,67,54,0.06) 100%)')
                  : adaptiveColor(theme, 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)', 'linear-gradient(135deg, rgba(255,152,0,0.12) 0%, rgba(255,152,0,0.06) 100%)'),
              border: `2px solid ${childAccountDialog.completedCount === childAccountDialog.totalCount
                ? '#4caf50'
                : childAccountDialog.completedCount === 0
                  ? '#f44336'
                  : '#ff9800'
              }`
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              {childAccountDialog.completedCount === childAccountDialog.totalCount ? (
                <DoneIcon sx={{ fontSize: 32, color: 'success.main' }} />
              ) : childAccountDialog.completedCount === 0 ? (
                <PendingIcon sx={{ fontSize: 32, color: 'error.main' }} />
              ) : (
                <InfoIcon sx={{ fontSize: 32, color: 'warning.main' }} />
              )}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                  {childAccountDialog.completedCount === childAccountDialog.totalCount
                    ? 'すべての子アカウント面接が完了しています'
                    : childAccountDialog.completedCount === 0
                      ? ' 子アカウントの面接が未完了です'
                      : ' 一部の子アカウント面接が未完了です'
                  }
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  完了状況: {childAccountDialog.completedCount} / {childAccountDialog.totalCount} 人
                </Typography>
              </Box>
            </Box>

            {/* 詳細メッセージ */}
            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
              {childAccountDialog.completedCount === childAccountDialog.totalCount
                ? '他の面接官による評価結果も含めて、総合的な面接評価を生成します。複数の視点からの評価により、より精度の高い判定が可能になります。'
                : childAccountDialog.completedCount === 0
                  ? '他の面接官からの評価がまだ提出されていません。現在の評価のみで進めることも可能ですが、より良い判定のため他の面接官の完了をお待ちいただくことをお勧めします。'
                  : `${childAccountDialog.totalCount - childAccountDialog.completedCount}人の面接官からの評価がまだ提出されていません。現在提出済みの評価と合わせて総合判定を行うことも可能です。`
              }
            </Typography>
          </Paper>

          {/* 子アカウント一覧 */}
          {childAccountDialog.childAccounts && childAccountDialog.childAccounts.length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
                面接官一覧
              </Typography>
              <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <List sx={{ py: 0 }}>
                  {childAccountDialog.childAccounts.map((account, index) => (
                    <React.Fragment key={index}>
                      <ListItem sx={{ py: 2 }}>
                        <ListItemIcon>
                          {account.is_completed ? (
                            <DoneIcon sx={{ color: 'success.main' }} />
                          ) : (
                            <PendingIcon sx={{ color: 'warning.main' }} />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                              {account.email || `面接官 ${index + 1}`}
                            </Typography>
                          }
                          secondary={
                            <Typography
                              variant="body2"
                              sx={{
                                color: account.is_completed ? 'success.main' : 'warning.main',
                                fontWeight: 'medium'
                              }}
                            >
                              {account.is_completed ? '✅ 評価完了' : '⏳ 評価待ち'}
                              {account.completed_at && (
                                <span style={{ marginLeft: 8, color: 'text.secondary' }}>
                                  ({new Date(account.completed_at).toLocaleString()})
                                </span>
                              )}
                            </Typography>
                          }
                        />
                      </ListItem>
                      {index < childAccountDialog.childAccounts.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </Paper>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={handleCancelChildAccountDialog}
            variant="outlined"
            color="inherit"
            sx={{
              borderRadius: 2,
              px: 3,
              textTransform: 'none'
            }}
          >
            キャンセル
          </Button>
          <Button
            onClick={handleProceedWithChildAccountStatus}
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 4,
              textTransform: 'none',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)'
              }
            }}
          >
            {childAccountDialog.completedCount === childAccountDialog.totalCount
              ? '統合評価を開始'
              : '現在の状況で評価を開始'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MainContent; 